import React from "react";
import { useDrag, useDrop } from "react-dnd";
import {
  ExerciseSiderCard,
  ExerciseSiderCardImage,
  ExerciseSiderCardDetails,
  ExerciseSiderCardTitle,
  ExerciseSiderCardDescription,
  ExerciseCard,
  ExerciseCardImage,
  ExerciseCardRightContainer,
  ExerciseCardLeftContainer,
  ExerciseCardRowWrapper,
  ExerciseDragDropTitle,
  StyledRemoveButton,
} from "../style";
import { IncrementNumberField, NumberDropdownField, NumberDropdownInput } from "../../../../../../../../../components/inputs";
import { StyledInputLabel } from "../../../../../../../../../components/inputs/style";
import { ReactComponent as CrossCircleIcon } from "../../../../../../../../../assets/icons/CrossCircleSmall.svg";
import { useTranslation } from "react-i18next";
import { IExercise } from "../../../../../../../../../interfaces/store/exercise-interface";
import { IMAGE_BASE_URL, VIDEO_BASE_URL } from "../../../../../../../../../common/config";
import { ISet, ISetType, IWorkoutExercise, IWorkoutPart } from "../../../../../../../../../interfaces/store/workout-interface";

export const DraggableExerciseSiderCard = ({ exercise }: { exercise: IExercise }) => {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "exercise",
      item: { exercise },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [exercise]
  );

  const videoName = exercise.video?.replace(/\.mp4$/, "") || "";

  const imgSrc = exercise.image ? `${IMAGE_BASE_URL}/${exercise.image}` : videoName ? `${VIDEO_BASE_URL}/${videoName}__thumb_regular.jpg` : undefined;

  return (
    <ExerciseSiderCard
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
      }}
    >
      <ExerciseSiderCardImage src={imgSrc} />
      <ExerciseSiderCardDetails>
        <ExerciseSiderCardTitle>{exercise.name}</ExerciseSiderCardTitle>
        <ExerciseSiderCardDescription>{exercise.description}</ExerciseSiderCardDescription>
      </ExerciseSiderCardDetails>
    </ExerciseSiderCard>
  );
};

type DraggableExerciseCardProps = {
  exercise: IWorkoutExercise;
  part: IWorkoutPart;
  index: number;
  moveItem: (partKey: string, dragIndex: number, hoverIndex: number) => void;
  handleRemove: (partID: string, index: number) => void;
  isEditable: boolean;
  setDroppedExercises: any;
};

export const DraggableExerciseCard = ({ exercise, part, index, moveItem, handleRemove, isEditable, setDroppedExercises }: DraggableExerciseCardProps) => {
  const { t } = useTranslation();
  const ref = React.useRef<HTMLDivElement>(null);

  const [, drag] = useDrag({
    type: "exercise",
    item: { id: exercise?._id, partId: part._id, index },
  });

  const [, drop] = useDrop({
    accept: "exercise",
    hover: (draggedItem: { id: string; partId: string; index: number }) => {
      if (part._id && draggedItem.id && draggedItem.id !== exercise?._id) {
        const { partId, index: dragIndex } = draggedItem;
        moveItem(partId, dragIndex, index);
        draggedItem.index = index;
        draggedItem.partId = part._id;
      }
    },
  });

  drag(drop(ref));

  const setDroppedExercisesContent = (content: Partial<IWorkoutExercise>) => {
    setDroppedExercises((prev: IWorkoutPart[]) =>
      prev.map((currPart) => {
        if (currPart._id === part._id) {
          return {
            ...currPart,
            exercises: currPart.exercises.map((currExercise, currExerciseIndex) => {
              if (currExerciseIndex === index) {
                return { ...currExercise, ...content };
              }
              return currExercise;
            }),
          };
        }
        return currPart;
      })
    );
  };

  const handleSetsChange = (sets: number) => {
    setDroppedExercisesContent({
      sets: Array.from({ length: sets }, (_, index) => {
        if (exercise.sets?.[index]) {
          return exercise.sets[index];
        }

        const previousSet = index > 0 ? exercise.sets?.[index - 1] : null;

        return {
          type: previousSet?.type || "repetitions",
          value: previousSet?.value || 0,
        };
      }),
    });
  };

  const handleSetChange = (inputValue: number, selectValue: ISetType, setIndex: number) => {
    setDroppedExercisesContent({
      sets: exercise.sets?.map((set, index) => {
        if (index === setIndex) {
          return {
            type: selectValue,
            value: inputValue,
          };
        }
        return set;
      }),
    });
  };

  const handleRestTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDroppedExercisesContent({
      restTime: parseInt(e.target.value) || 0,
    });
  };

  const setOptions = [
    { value: "repetitions", label: t("Reps") },
    { value: "seconds", label: t("Seconds") },
    { value: "minutes", label: t("Minutes") },
    { value: "meter", label: t("Meter") },
    { value: "kilometer", label: t("Kilometer") },
  ];

  const partsWithExercisesRestTime = ["dropSet", "training", "intervals", "finisher", "running", "sprint"];

  const partsWithExercisesWithOneSet = ["EMOM", "AMRAP", "superSet", "warmUp", "stretches", "dynamicWarmup", "core", "mobility", "activation", "tripleSet", "metcon"];

  const partsWithExercisesWithNoSets = ["TABATA", "stations"];

  const videoName = exercise.video?.replace(/\.mp4$/, "") || "";

  const imgSrc = exercise.image ? `${IMAGE_BASE_URL}/${exercise.image}` : videoName ? `${VIDEO_BASE_URL}/${videoName}__thumb_regular.jpg` : undefined;

  return (
    <ExerciseCard ref={ref} isEditable={isEditable}>
      <ExerciseCardRightContainer>
        <ExerciseCardImage src={imgSrc} />
      </ExerciseCardRightContainer>
      <ExerciseCardLeftContainer>
        <ExerciseCardRowWrapper style={{ justifyContent: "space-between" }}>
          <ExerciseDragDropTitle>{exercise.name}</ExerciseDragDropTitle>
          {isEditable && <StyledRemoveButton icon={<CrossCircleIcon />} onClick={() => handleRemove(part._id || "", index)} />}
        </ExerciseCardRowWrapper>
        {![...partsWithExercisesWithOneSet, ...partsWithExercisesWithNoSets].includes(part.name) && (
          <ExerciseCardRowWrapper>
            <IncrementNumberField label={t("Sets")} isEditable={isEditable} value={exercise.sets?.length || 0} onChange={(value) => handleSetsChange(value)} />
          </ExerciseCardRowWrapper>
        )}

        <ExerciseCardRowWrapper>
          {exercise.sets?.map((set: ISet, index: number) => (
            <NumberDropdownField
              key={index}
              isEditable={isEditable}
              value={set.value}
              options={setOptions}
              defaultOption={set.type || "repetitions"}
              onChange={(inputValue, selectValue) => handleSetChange(inputValue || 0, selectValue as ISetType, index)}
            />
          ))}
        </ExerciseCardRowWrapper>
        {partsWithExercisesRestTime.includes(part.name) && (
          <ExerciseCardRowWrapper>
            <StyledInputLabel>{t("Rest between sets")}</StyledInputLabel>
            <NumberDropdownInput value={exercise.restTime || ""} onChange={(e) => handleRestTimeChange(e)} />
          </ExerciseCardRowWrapper>
        )}
      </ExerciseCardLeftContainer>
    </ExerciseCard>
  );
};
